import React from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../components/ui/PageHeader';
import { SectionHeading } from '../components/ui/SectionHeading';
import { StepList } from '../components/StepList';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Accessibility as AccessibilityIcon, ArrowRight, Users, ShieldCheck, CheckCircle2, Search, ClipboardList, MessageCircle, Wrench } from 'lucide-react';
const ACCESSIBILITE_STEPS = [{
  title: 'Identification des besoins',
  description: "Échange confidentiel avec le référent handicap avant ou lors de l'inscription pour évaluer les besoins spécifiques.",
  icon: <Search className="w-6 h-6" data-id="element-729" data-mp="2yxoy13qijbkt" />
}, {
  title: "Plan d'accompagnement individualisé",
  description: 'Définition des aménagements pédagogiques, techniques ou organisationnels nécessaires.',
  icon: <ClipboardList className="w-6 h-6" data-id="element-730" data-mp="e7zg2huij9nw" />
}, {
  title: 'Réunion de concertation',
  description: "Implication de l'équipe pédagogique et, si besoin, de l'entreprise d'accueil pour assurer la cohérence du parcours.",
  icon: <MessageCircle className="w-6 h-6" data-id="element-731" data-mp="2zi4vgatz3sfe" />
}, {
  title: 'Déploiement des aménagements',
  description: 'Mise en place effective des solutions (tiers temps, matériel adapté, supports spécifiques, etc.).',
  icon: <Wrench className="w-6 h-6" data-id="element-732" data-mp="26tz8rijcztcx" />
}];
export function Accessibilite() {
  useSeo({
    title: 'Accessibilité et handicap — Référent handicap du CFA',
    description: 'Zillenium Alternance accueille les personnes en situation de handicap : étude individualisée des besoins, aménagements pédagogiques et matériels, référent handicap dédié et partenaires spécialisés.',
    path: '/accessibilite'
  });
  return <div className="flex flex-col w-full" data-id="element-733" data-mp="1o1wm9qk1diur">
      <PageHeader title="Accessibilité et Handicap" subtitle="Zillenium Alternance s'engage pour un accueil inclusif et la réussite de tous les apprenants." data-id="element-734" data-mp="1o1535f7mldld" />

      <div className="py-16 md:py-24 bg-white relative overflow-hidden" data-id="element-735" data-mp="1kzjwu9l6f7m5">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-brand-orange rounded-full blur-[100px] opacity-10 pointer-events-none" data-id="element-736" data-mp="1g1ib1gwd8u6z" />
        <div className="container max-w-5xl mx-auto relative z-10" data-id="element-737" data-mp="1ut0n53m3u4l2">
          {/* Engagement */}
          <section className="flex flex-col md:flex-row gap-10 items-center bg-white shadow-xl p-8 md:p-12 rounded-3xl mb-24 border-none group hover:-translate-y-2 transition-transform duration-300" data-id="element-738" data-mp="1pufnp6eofoxo">
            <div className="w-24 h-24 bg-lavande rounded-3xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-300" data-id="element-739" data-mp="x5s8nskitvsk">
              <AccessibilityIcon className="w-12 h-12 text-brand-orange animate-soft-bounce" data-id="element-740" data-mp="1700mda4w7k6o" />
            </div>
            <div data-id="element-741" data-mp="2ne0kqgo3b1rt">
              <h2 className="text-3xl font-extrabold font-heading text-primary-dark mb-4" data-id="element-742" data-mp="2nhoe9ftjbo6j">
                Notre engagement d'accueil inclusif
              </h2>
              <p className="text-xl text-muted-foreground leading-relaxed" data-id="element-743" data-mp="2pzi4xnnc5sgd">
                Nous mettons tout en œuvre pour rendre nos formations
                accessibles aux personnes en situation de handicap. Notre
                objectif est de garantir l'égalité des chances en adaptant nos
                parcours de formation et nos modalités d'évaluation aux besoins
                spécifiques de chacun.
              </p>
            </div>
          </section>

          {/* Processus */}
          <section className="mb-24" data-id="element-744" data-mp="3doj6qs1ny5co">
            <SectionHeading title="Le processus d'accompagnement" className="mb-12" data-id="element-745" data-mp="3drowm9i49b9g" />
            <StepList steps={ACCESSIBILITE_STEPS} data-id="element-746" data-mp="33zj14psnobpi" />
          </section>

          <div className="grid md:grid-cols-2 gap-10 mb-24" data-id="element-747" data-mp="33xojplglf1q6">
            {/* Suivi */}
            <Card className="border-none shadow-xl bg-white hover:-translate-y-2 transition-transform duration-300 group" data-id="element-748" data-mp="3rnitfu5m8a67">
              <div className="h-2 w-full bg-primary" data-id="element-749" data-mp="28rqvgnb396v2" />
              <CardContent className="p-8" data-id="element-750" data-mp="28tle1uyw0mvz">
                <h3 className="text-2xl font-extrabold font-heading text-primary-dark mb-6 flex items-center gap-4" data-id="element-751" data-mp="23t75mbtdjaxf">
                  <div className="w-12 h-12 rounded-xl bg-lavande flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-752" data-mp="1yt2pwo0aik89">
                    <CheckCircle2 className="w-6 h-6 text-primary animate-soft-pulse" data-id="element-753" data-mp="1uu2c5jwo67s4" />
                  </div>
                  Suivi du parcours
                </h3>
                <p className="text-lg text-muted-foreground mb-8 leading-relaxed" data-id="element-754" data-mp="2mkgimv2cshtr">
                  Un suivi régulier est assuré tout au long de la formation avec
                  3 rendez-vous annuels dédiés :
                </p>
                <ul className="space-y-4" data-id="element-755" data-mp="2dp0t2q8msotz">
                  <li className="flex items-center gap-4 bg-muted p-4 rounded-xl" data-id="element-756" data-mp="3a9jb6xyizv31">
                    <div className="w-3 h-3 rounded-full bg-brand-orange animate-soft-pulse" data-id="element-757" data-mp="35ijcbn5uj2xw"></div>
                    <span className="font-bold text-primary-dark" data-id="element-758" data-mp="rzwrs379u8hy">
                      En début de parcours
                    </span>
                  </li>
                  <li className="flex items-center gap-4 bg-muted p-4 rounded-xl" data-id="element-759" data-mp="wyzz5uaq4qt3">
                    <div className="w-3 h-3 rounded-full bg-brand-orange animate-soft-pulse" style={{
                    animationDelay: '0.5s'
                  }} data-id="element-760" data-mp="10zjxh171a0gt"></div>
                    <span className="font-bold text-primary-dark" data-id="element-761" data-mp="1fxm11be2ely2">
                      À mi-parcours
                    </span>
                  </li>
                  <li className="flex items-center gap-4 bg-muted p-4 rounded-xl" data-id="element-762" data-mp="1kpltyjvpwel5">
                    <div className="w-3 h-3 rounded-full bg-brand-orange animate-soft-pulse" style={{
                    animationDelay: '1s'
                  }} data-id="element-763" data-mp="1oounbnmuiag6"></div>
                    <span className="font-bold text-primary-dark" data-id="element-764" data-mp="1ylzey2zgmy4d">
                      En fin de parcours
                    </span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Contacts & Partenaires */}
            <div className="space-y-10" data-id="element-765" data-mp="8cfciz4yfpbh">
              <Card className="bg-primary-dark text-white border-none shadow-xl hover:-translate-y-2 transition-transform duration-300 group" data-id="element-766" data-mp="8dgpj95j6lvu">
                <div className="h-2 w-full bg-primary-accent" data-id="element-767" data-mp="hbah66scorco" />
                <CardContent className="p-8" data-id="element-768" data-mp="daqkf4bovpr2">
                  <h3 className="text-2xl font-extrabold font-heading mb-6 flex items-center gap-4" data-id="element-769" data-mp="d9ypzwttvy2d">
                    <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-770" data-mp="r96th1x0xscd">
                      <Users className="w-6 h-6 text-lavande animate-soft-float" data-id="element-771" data-mp="2t1uztk4ysvwd" />
                    </div>
                    Vos référents handicap
                  </h3>
                  <p className="text-lg text-lavande mb-6 leading-relaxed" data-id="element-772" data-mp="2o4w4wzdh8kx4">
                    Nos référents sont à votre écoute pour construire avec vous
                    votre parcours de formation.
                  </p>
                  <div className="space-y-3 mb-8" data-id="element-773" data-mp="tuxryz7yd1qs">
                    <div className="font-bold text-xl" data-id="element-774" data-mp="tueim6wdblod">Samir MOKEDDEM</div>
                    <div className="font-bold text-xl" data-id="element-775" data-mp="yt8yazz1gu49">Fadma JIBJIB</div>
                  </div>
                  <a href="mailto:contact@zilleniumalternance.fr" className="text-brand-orange font-bold hover:text-white transition-colors underline underline-offset-4 text-lg" data-id="element-776" data-mp="ktztewga9mfl">
                    Contacter les référents
                  </a>
                </CardContent>
              </Card>

              <div className="bg-white p-8 rounded-3xl shadow-lg border-none" data-id="element-777" data-mp="fx96bp629qko">
                <h3 className="font-extrabold font-heading text-xl text-primary-dark mb-4" data-id="element-778" data-mp="25aw3rauim1vp">
                  Nos partenaires relais
                </h3>
                <p className="text-muted-foreground mb-6 leading-relaxed" data-id="element-779" data-mp="y0c6dgavpoao">
                  Nous travaillons en étroite collaboration avec les acteurs
                  spécialisés du territoire :
                </p>
                <div className="flex flex-wrap gap-3" data-id="element-780" data-mp="o8zhmxkdne8v">
                  <span className="text-sm font-bold bg-lavande text-primary-dark px-3 py-1.5 rounded-lg" data-id="element-781" data-mp="jaxy6bj1hcwv">
                    AGEFIPH BFC
                  </span>
                  <span className="text-sm font-bold bg-lavande text-primary-dark px-3 py-1.5 rounded-lg" data-id="element-782" data-mp="edejxbmcd04x">
                    Mission Locale
                  </span>
                  <span className="text-sm font-bold bg-lavande text-primary-dark px-3 py-1.5 rounded-lg" data-id="element-783" data-mp="5adfrgh1dj3w">
                    Cap Emploi
                  </span>
                  <span className="text-sm font-bold bg-lavande text-primary-dark px-3 py-1.5 rounded-lg" data-id="element-784" data-mp="eesxxbcw95l">
                    MDPH
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Droits & RGPD */}
          <section className="space-y-10 bg-muted p-8 md:p-12 rounded-3xl" data-id="element-785" data-mp="2292x4lrqcxh9">
            <div data-id="element-786" data-mp="277xgppxiuq2f">
              <h3 className="text-2xl font-extrabold font-heading text-primary-dark mb-6" data-id="element-787" data-mp="277e3unmqarv9">
                Les droits de l'apprenant
              </h3>
              <ul className="list-disc pl-6 space-y-3 text-lg text-muted-foreground marker:text-brand-orange" data-id="element-788" data-mp="2gy8ue470cdmi">
                <li data-id="element-789" data-mp="1jb9rvctmwbgp">Droit à la non-discrimination à l'accès à la formation.</li>
                <li data-id="element-790" data-mp="1o9uwrpjtzjbh">
                  Droit à l'aménagement des épreuves d'examen (tiers temps,
                  assistance, etc.).
                </li>
                <li data-id="element-791" data-mp="1t7cuejseec46">Droit à l'adaptation du poste de travail en entreprise.</li>
                <li data-id="element-792" data-mp="1y925u0jj8rss">Droit à la confidentialité des informations médicales.</li>
              </ul>
            </div>

            <div className="flex gap-6 items-start bg-white p-8 rounded-2xl shadow-sm border-none group" data-id="element-793" data-mp="34nbb99tti2fy">
              <div className="w-12 h-12 rounded-xl bg-lavande flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform" data-id="element-794" data-mp="39lmirdwn40v0">
                <ShieldCheck className="w-6 h-6 text-primary animate-soft-pulse" data-id="element-795" data-mp="3tqo4d79ij9km" />
              </div>
              <p className="text-muted-foreground leading-relaxed" data-id="element-796" data-mp="2bqjvy2hyuir">
                <strong className="text-primary-dark font-bold text-lg block mb-2" data-id="element-797" data-mp="2el3jndosbb4">
                  Confidentialité (RGPD)
                </strong>
                Les informations relatives à votre situation de handicap sont
                strictement confidentielles. Elles ne sont partagées avec
                l'équipe pédagogique ou l'entreprise qu'avec votre accord
                explicite, et uniquement dans le but de mettre en place les
                aménagements nécessaires.
              </p>
            </div>

            <div className="text-center pt-8" data-id="element-798" data-mp="v1fmgfuuv2mh">
              <Button size="lg" className="group bg-brand-orange hover:bg-[#E67E22] text-white text-lg px-8 py-7 shadow-xl" asChild data-id="element-799" data-mp="v0o1s02sqpaz">
                <Link to="/candidater" data-id="element-800" data-mp="2khi4jmfexf1v">
                  Remplir le formulaire etudiant
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-801" data-mp="2fk9q9wxdxv5w" />
                </Link>
              </Button>
            </div>
          </section>
        </div>
      </div>
    </div>;
}